import {objectFromEntries, range} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import {assertNever, JSONValue} from 'util/Types';

import {
  EnumUISchema,
  FieldUISchemaWithStandardComponent,
  getFieldSettingsIfPresent,
  ShapeUISchema,
  StepConfigUISchema,
} from '../StepConfigEditorTypes';

export default function jsonSpecToInitialValue(
  jsonSpec: PipelineConfigJsonSpec,
  uiSchema: StepConfigUISchema | undefined
): JSONValue {
  if (jsonSpec.kind === 'string') {
    if (jsonSpec.value) {
      return jsonSpec.value;
    } else {
      return null;
    }
  } else if (jsonSpec.kind === 'number') {
    return null;
  } else if (jsonSpec.kind === 'integer') {
    return null;
  } else if (jsonSpec.kind === 'boolean') {
    return null;
  } else if (jsonSpec.kind === 'date') {
    return null;
  } else if (jsonSpec.kind === 'columnReference') {
    return null;
  } else if (jsonSpec.kind === 'columnValue') {
    return null;
  } else if (jsonSpec.kind === 'tableReference') {
    return null;
  } else if (jsonSpec.kind === 'domain') {
    return null;
  } else if (jsonSpec.kind === 'anything') {
    return null;
  } else if (jsonSpec.kind === 'array') {
    // For array specs, pre-populate based on minItems only if the element type can be meaningfully initialized.
    // Tagged unions can be null since they get populated later
    // Other null-initializing types (domain, enum without default, etc.) should NOT be pre-populated
    //
    // V2 schemas wrap element schemas inside an `elementUISchema` property on the field schema.
    // Unwrap so the recursive call receives the correct element-level schema.
    const elementSchema =
      uiSchema != null &&
      uiSchema.hasOwnProperty('configShapePath') &&
      uiSchema.hasOwnProperty('elementUISchema')
        ? (uiSchema as FieldUISchemaWithStandardComponent).elementUISchema
        : uiSchema;
    const elementInitialValue = jsonSpecToInitialValue(jsonSpec.elementSpec, elementSchema);

    if (jsonSpec.elementSpec.kind === 'taggedUnion' || elementInitialValue != null) {
      return range(1, jsonSpec.minItems ?? 0).map(() => elementInitialValue);
    }
    return [];
  } else if (jsonSpec.kind === 'enum') {
    return (uiSchema?.hasOwnProperty('defaultValue') ?? false)
      ? ((uiSchema as EnumUISchema | undefined)?.defaultValue ?? null)
      : null;
  } else if (jsonSpec.kind === 'taggedUnion') {
    return null;
  } else if (jsonSpec.kind === 'shape') {
    return objectFromEntries(
      Object.entries(jsonSpec.fields).map(([fieldName, fieldSpec]) => {
        const fieldSettings = getFieldSettingsIfPresent(uiSchema as ShapeUISchema | undefined, fieldName);
        return [fieldName, jsonSpecToInitialValue(fieldSpec, fieldSettings)];
      })
    );
  } else {
    assert(false, `Unexpected jsonSpec ${assertNever(jsonSpec)}`);
  }
}
